import React from "react";

/**
 * ReviewLytics brand logo.
 *
 * @param {{
 *   size?: number;              // pixel size of the square badge mark
 *   showWordmark?: boolean;     // render the "ReviewLytics" wordmark next to the mark
 *   variant?: "light" | "dark"; // wordmark colors: light = dark text (for light bg), dark = white text (for dark bg)
 *   tagline?: string;           // optional small caps line under the wordmark
 *   className?: string;
 * }} props
 */
const BrandLogo = ({
  size = 36,
  showWordmark = false,
  variant = "light",
  tagline,
  className = "",
}) => {
  const wordDark = variant === "dark";
  const reviewColor = wordDark ? "#FFFFFF" : "#0F172A";
  const lyticsColor = wordDark ? "#5EEAD4" : "#0D9488";
  const taglineColor = wordDark ? "#94A3B8" : "#6B7280";

  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-hidden={showWordmark ? true : undefined}
      role={showWordmark ? undefined : "img"}
      aria-label={showWordmark ? undefined : "ReviewLytics"}
    >
      <defs>
        <linearGradient id="rlBadgeGrad" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop stopColor="#13A697" />
          <stop offset="1" stopColor="#045C54" />
        </linearGradient>
        <linearGradient id="rlBarGrad" x1="32" y1="18" x2="32" y2="46" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#CFF8F0" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="url(#rlBadgeGrad)" />
      <rect x="4.75" y="4.75" width="54.5" height="54.5" rx="15.25" fill="none" stroke="#FFFFFF" strokeOpacity="0.16" strokeWidth="1.5" />
      <rect x="16" y="34" width="7" height="11" rx="3.5" fill="url(#rlBarGrad)" fillOpacity="0.82" />
      <rect x="28.5" y="28" width="7" height="17" rx="3.5" fill="url(#rlBarGrad)" fillOpacity="0.9" />
      <rect x="41" y="22" width="7" height="23" rx="3.5" fill="url(#rlBarGrad)" />
      <path
        d="M44.5 9.5 C45.1 14.2 46.3 15.4 51 16 C46.3 16.6 45.1 17.8 44.5 22.5 C43.9 17.8 42.7 16.6 38 16 C42.7 15.4 43.9 14.2 44.5 9.5 Z"
        fill="#FFFFFF"
      />
    </svg>
  );

  if (!showWordmark) {
    return <span className={className}>{mark}</span>;
  }

  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      {mark}
      <span className="min-w-0 leading-none">
        <span
          className="block font-extrabold tracking-[-0.02em]"
          style={{
            fontFamily: "Manrope, Inter, sans-serif",
            fontSize: Math.round(size * 0.62),
            lineHeight: 1,
          }}
        >
          <span style={{ color: reviewColor }}>Review</span>
          <span style={{ color: lyticsColor }}>Lytics</span>
        </span>
        {tagline ? (
          <span
            className="mt-1 block font-semibold uppercase"
            style={{
              fontSize: Math.max(9, Math.round(size * 0.2)),
              letterSpacing: "0.16em",
              color: taglineColor,
            }}
          >
            {tagline}
          </span>
        ) : null}
      </span>
    </span>
  );
};

export default BrandLogo;
